import {
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
} from '@blocksuite/affine-block-database';
import {
  AutomationConnectorTool,
  DEFAULT_AUTOMATION_HEIGHT,
  DEFAULT_AUTOMATION_WIDTH,
  DEFAULT_CLEANUP_SIZE,
} from '@blocksuite/affine-block-streamlain';
import { DefaultTool } from '@blocksuite/affine-block-surface';
import {
  type EdgelessToolbarI18nKey,
  edgelessToolbarI18nT,
  type ElementCreationSource,
  ThemeProvider,
} from '@blocksuite/affine-shared/services';
import {
  DragClickGuard,
  draggedCardPreview,
  EdgelessDraggableElementController,
  EdgelessToolbarToolMixin,
  SENIOR_TOOL_ART,
  SeniorToolExtension,
} from '@blocksuite/affine-widget-edgeless-toolbar';
import { Bound } from '@blocksuite/global/gfx';
import { SignalWatcher } from '@blocksuite/global/lit';
import { DualLinkIcon } from '@blocksuite/icons/lit';
import type { BlockComponent } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import {
  automationDenseIcon,
  automationRender,
  cleanupDenseIcon,
  cleanupRender,
  databaseDenseIcon,
  databaseRender,
  type StreamLainDraggableTool,
} from './streamlain-basket-elements.js';

const WORKFLOW_ENABLED_CLASS = 'workflow-enabled';

export type StreamLainFlowBlockKind = 'database' | 'automation' | 'cleanup';

export type StreamLainFlowMenuAction =
  | StreamLainFlowBlockKind
  | 'automationConnector';

type StreamLainFlowBlock = {
  type: StreamLainFlowBlockKind;
  render: StreamLainDraggableTool['render'];
  standardWidth: number;
  standardHeight: number;
  tooltipKey: EdgelessToolbarI18nKey;
};

const databaseBlock: StreamLainFlowBlock = {
  type: 'database',
  render: databaseRender,
  standardWidth: DEFAULT_CANVAS_DATABASE_WIDTH,
  standardHeight: DEFAULT_CANVAS_DATABASE_HEIGHT,
  tooltipKey: 'toolbar.database',
};

const automationBlock: StreamLainFlowBlock = {
  type: 'automation',
  render: automationRender,
  standardWidth: DEFAULT_AUTOMATION_WIDTH,
  standardHeight: DEFAULT_AUTOMATION_HEIGHT,
  tooltipKey: 'toolbar.automationBlock',
};

const cleanupBlock: StreamLainFlowBlock = {
  type: 'cleanup',
  render: cleanupRender,
  standardWidth: DEFAULT_CLEANUP_SIZE,
  standardHeight: DEFAULT_CLEANUP_SIZE,
  tooltipKey: 'toolbar.cleanup',
};

export function createStreamLainFlowBlockAtCenter(
  kind: StreamLainFlowBlockKind,
  edgeless: BlockComponent | undefined,
  control: ElementCreationSource = 'toolbar:general'
) {
  if (!edgeless) {
    return Promise.resolve(null);
  }
  if (kind !== 'database' && !isStreamLainWorkflowEnabled()) {
    return Promise.resolve(null);
  }

  const item =
    kind === 'cleanup'
      ? cleanupBlock
      : kind === 'automation'
        ? automationBlock
        : databaseBlock;
  const { x, y } = edgeless.std.get(GfxControllerIdentifier).viewport.center;
  const bound = new Bound(
    x - item.standardWidth / 2,
    y - item.standardHeight / 2,
    item.standardWidth,
    item.standardHeight
  );

  return item.render(bound, edgeless, control);
}

export function isStreamLainWorkflowEnabled() {
  return (
    typeof document !== 'undefined' &&
    document.body.classList.contains(WORKFLOW_ENABLED_CLASS)
  );
}

export class EdgelessStreamLainFlowMenu extends EdgelessToolbarToolMixin(
  LitElement
) {
  static override styles = css`
    :host {
      position: absolute;
      display: flex;
      z-index: -1;
    }

    .streamlain-flow-menu-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 14px;
    }

    .streamlain-flow-menu-content svg {
      display: block;
      width: 20px;
      height: 20px;
    }
  `;

  override type = AutomationConnectorTool;

  override render() {
    const workflowEnabled = isStreamLainWorkflowEnabled();

    return html`<edgeless-slide-menu>
      <div class="streamlain-flow-menu-content">
        <edgeless-tool-icon-button
          data-testid="streamlain-flow-menu-database"
          .activeMode=${'background'}
          .tooltip=${edgelessToolbarI18nT(databaseBlock.tooltipKey)}
          @click=${() => this.onSelect('database')}
        >
          ${databaseDenseIcon}
        </edgeless-tool-icon-button>

        ${workflowEnabled
          ? html`
              <edgeless-tool-icon-button
                data-testid="streamlain-flow-menu-automation"
                .activeMode=${'background'}
                .tooltip=${edgelessToolbarI18nT(automationBlock.tooltipKey)}
                @click=${() => this.onSelect('automation')}
              >
                ${automationDenseIcon}
              </edgeless-tool-icon-button>

              <edgeless-tool-icon-button
                data-testid="streamlain-flow-menu-cleanup"
                .activeMode=${'background'}
                .tooltip=${edgelessToolbarI18nT(cleanupBlock.tooltipKey)}
                @click=${() => this.onSelect('cleanup')}
              >
                ${cleanupDenseIcon}
              </edgeless-tool-icon-button>

              <edgeless-tool-icon-button
                data-testid="streamlain-flow-menu-connector"
                .active=${this.active}
                .activeMode=${'background'}
                .tooltip=${edgelessToolbarI18nT('toolbar.automationConnector')}
                @click=${() => this.onSelect('automationConnector')}
              >
                ${DualLinkIcon()}
              </edgeless-tool-icon-button>
            `
          : null}
      </div>
    </edgeless-slide-menu>`;
  }

  @property({ attribute: false })
  accessor onSelect!: (action: StreamLainFlowMenuAction) => void;
}

/** A block the tool's artwork offers as a card: it drags out of the dock onto the canvas. */
interface StreamLainFlowCard {
  block: StreamLainFlowBlock;
  art: TemplateResult;
}

const databaseCard: StreamLainFlowCard = {
  block: databaseBlock,
  art: html`<svg
    viewBox="0 0 36 30"
    width="100%"
    height="100%"
    fill="none"
    style="display:block"
  >
    <rect
      x="5"
      y="5"
      width="26"
      height="6"
      rx="1.5"
      fill="var(--streamlain-v2-brand-primary)"
      opacity="0.85"
    />
    <path
      d="M5 16h26M5 22h26M14 13v12"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
    />
  </svg>`,
};

const automationCard: StreamLainFlowCard = {
  block: automationBlock,
  art: html`<svg
    viewBox="0 0 36 30"
    width="100%"
    height="100%"
    fill="none"
    style="display:block"
  >
    <circle
      cx="12"
      cy="15"
      r="7"
      fill="var(--streamlain-v2-brand-primary)"
      opacity="0.85"
    />
    <path d="M10.2 11.8v6.4l5.2-3.2z" fill="#fff" />
    <path
      d="M22 11h8M22 15h6M22 19h8"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
    />
  </svg>`,
};

const gearArt = html`<svg viewBox="0 0 34 34">
  <path
    fill="currentColor"
    stroke="currentColor"
    stroke-width="2"
    stroke-linejoin="round"
    d="M29.03 14.01L32.84 14.75L32.84 19.25L29.03 19.99L27.62 23.4L29.79 26.61L26.61 29.79L23.4 27.62L19.99 29.03L19.25 32.84L14.75 32.84L14.01 29.03L10.6 27.62L7.39 29.79L4.21 26.61L6.38 23.4L4.97 19.99L1.16 19.25L1.16 14.75L4.97 14.01L6.38 10.6L4.21 7.39L7.39 4.21L10.6 6.38L14.01 4.97L14.75 1.16L19.25 1.16L19.99 4.97L23.4 6.38L26.61 4.21L29.79 7.39L27.62 10.6Z"
  />
  <circle cx="17" cy="17" r="8.5" fill="var(--art-card)" />
</svg>`;

const boltArt = html`<svg viewBox="0 0 34 34">
  <path d="M18.6 9.5l-6.1 8.4h4l-1.3 6.6 6.3-8.6h-4.1z" fill="#f5a524" />
</svg>`;

/** The cards the artwork shows: every block the tool can create now. */
function flowCards(): StreamLainFlowCard[] {
  return isStreamLainWorkflowEnabled()
    ? [databaseCard, automationCard]
    : [databaseCard];
}

/** The artwork at rest: the blocks as cards around the gear. */
const FLOW_ART = css`
  .flow-card {
    width: 36px;
    height: 30px;
    border-radius: 4px;
  }
  .flow-card.database {
    --x: -21px;
    --y: -13px;
    --r: -8deg;
    transform-origin: 100% 100%;
  }
  .flow-card.automation {
    --x: 21px;
    --y: -15px;
    --r: 8deg;
    transform-origin: 0% 100%;
  }
  .flow-card:only-of-type {
    --x: -3px;
    --y: -20px;
    --r: -5deg;
    transform-origin: 50% 100%;
  }
  .flow-gear,
  .flow-bolt {
    width: 36px;
    height: 36px;
    --y: 8px;
  }
  .flow-gear {
    color: var(--art-ink);
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.28));
  }
  .flow-bolt {
    pointer-events: none;
  }
`;

/**
 * The tool's artwork as a picture outside the dock, e.g. where a list of automations is still
 * empty: what the dock shows at rest, without taking input.
 */
export class StreamLainFlowArt extends LitElement {
  static override styles = [
    SENIOR_TOOL_ART,
    FLOW_ART,
    css`
      :host {
        display: block;
        width: 96px;
        /* The gear stands below the stage's edge as in the dock; here nothing cuts it off. */
        padding-bottom: 8px;
      }
      .senior-art {
        height: 52px;
        cursor: default;
        clip-path: none;
      }
    `,
  ];

  @property({ attribute: 'app-theme' })
  accessor appTheme: 'light' | 'dark' = 'light';

  override render() {
    return html`<div class="senior-art" data-app-theme=${this.appTheme}>
      ${flowCards().map(
        card =>
          html`<div class="art-part art-card flow-card ${card.block.type}">
            ${card.art}
          </div>`
      )}
      <div class="art-part flow-gear">${gearArt}</div>
      <div class="art-part flow-bolt">
        ${isStreamLainWorkflowEnabled() ? boltArt : nothing}
      </div>
    </div>`;
  }
}

/**
 * The databases-and-automations tool in the dock: a gear with the blocks it offers as cards. A card
 * drags onto the canvas; a click opens the menu with everything the tool holds.
 */
export class EdgelessStreamLainFlowToolButton extends EdgelessToolbarToolMixin(
  SignalWatcher(LitElement)
) {
  static override styles = [
    SENIOR_TOOL_ART,
    FLOW_ART,
    css`
      :host,
      .edgeless-streamlain-flow-button {
        display: block;
        width: 100%;
        height: 100%;
      }

      .flow-card {
        cursor: grab;
      }
      .flow-card.dragged {
        opacity: 0;
      }
      .flow-gear svg {
        transition: transform 0.6s cubic-bezier(0.3, 1.4, 0.5, 1);
      }

      .senior-art:hover .flow-card.database,
      .senior-art.open .flow-card.database {
        --x: -28px;
        --y: -33px;
        --r: -15deg;
      }
      .senior-art:hover .flow-card.automation,
      .senior-art.open .flow-card.automation {
        --x: 28px;
        --y: -36px;
        --r: 15deg;
      }
      .senior-art:hover .flow-card:only-of-type,
      .senior-art.open .flow-card:only-of-type {
        --x: -4px;
        --y: -38px;
        --r: -9deg;
      }
      .senior-art:hover .flow-gear,
      .senior-art.open .flow-gear,
      .senior-art:hover .flow-bolt,
      .senior-art.open .flow-bolt {
        --y: 2px;
      }
      .senior-art:hover .flow-gear svg,
      .senior-art.open .flow-gear svg {
        transform: rotate(135deg);
      }
      .senior-art .flow-card:hover {
        --s: 1.1;
      }
    `,
  ];

  private readonly _clickGuard = new DragClickGuard(this);

  draggableController!: EdgelessDraggableElementController<StreamLainFlowCard>;

  override enableActiveBackground = true;

  override type = AutomationConnectorTool;

  get cards(): StreamLainFlowCard[] {
    return flowCards();
  }

  private _disposeMenu() {
    if (!this.popper) return false;
    this.popper.dispose();
    this.popper = null;
    return true;
  }

  private _toggleMenu() {
    if (this._clickGuard.endsCalledOffDrag()) return;
    if (this._disposeMenu()) return;

    const menu = this.createPopper('edgeless-streamlain-flow-menu', this);
    menu.element.edgeless = this.edgeless;
    menu.element.onSelect = action => {
      if (action === 'automationConnector') {
        this.setEdgelessTool(AutomationConnectorTool);
        this._disposeMenu();
        return;
      }

      createStreamLainFlowBlockAtCenter(action, this.edgeless)
        .then(id => {
          if (id) this.setEdgelessTool(DefaultTool);
        })
        .catch(console.error)
        .finally(() => this._disposeMenu());
    };
  }

  private _initDragController() {
    if (!this.edgeless || !this.toolbarContainer) return;
    if (this.draggableController) return;
    this.draggableController = new EdgelessDraggableElementController(this, {
      edgeless: this.edgeless,
      scopeElement: this.toolbarContainer,
      clickToDrag: false,
      onCanceled: this._clickGuard.calledOff,
      // The block lands with the middle of its top edge under the pointer.
      onDrop: (card, bound, [x, y]) => {
        const { block } = card.data;
        block
          .render(
            new Bound(x - block.standardWidth / 2, y, bound.w, bound.h),
            this.edgeless
          )
          .then(id => {
            if (id) this.setEdgelessTool(DefaultTool);
          })
          .catch(console.error);
      },
    });
  }

  private _startDrag(event: MouseEvent | TouchEvent, card: StreamLainFlowCard) {
    const element = event.currentTarget as HTMLElement;
    const info = {
      data: card,
      preview: draggedCardPreview(element, card.art),
      standardWidth: card.block.standardWidth,
    };
    if (event instanceof MouseEvent) {
      this.draggableController.onMouseDown(event, info);
    } else {
      this.draggableController.onTouchStart(event, info);
    }
  }

  override render() {
    const appTheme = this.edgeless.std.get(ThemeProvider).app$.value;
    const dragged = this.draggableController?.states.draggingElement;
    const open = !!this.popper || !!dragged;

    return html`<edgeless-toolbar-button
      class="edgeless-streamlain-flow-button"
      data-testid="edgeless-streamlain-flow-tool-button"
      .tooltip=${open ? '' : edgelessToolbarI18nT('toolbar.automation')}
      .tooltipOffset=${5}
      @click=${this._toggleMenu}
    >
      <div class="senior-art ${open ? 'open' : ''}" data-app-theme=${appTheme}>
        ${repeat(
          this.cards,
          card => card.block.type,
          card =>
            html`<div
              class="art-part art-card flow-card ${card.block
                .type} ${dragged?.data === card ? 'dragged' : ''}"
              data-testid="streamlain-flow-card-${card.block.type}"
              @mousedown=${(event: MouseEvent) => this._startDrag(event, card)}
              @touchstart=${(event: TouchEvent) => this._startDrag(event, card)}
            >
              ${card.art}
            </div>`
        )}
        <div class="art-part flow-gear">${gearArt}</div>
        <div class="art-part flow-bolt">
          ${isStreamLainWorkflowEnabled() ? boltArt : nothing}
        </div>
      </div>
    </edgeless-toolbar-button>`;
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    if (changed.has('edgeless') || changed.has('toolbarContainer')) {
      this._initDragController();
    }
  }
}

export const streamLainFlowSeniorTool = SeniorToolExtension(
  'streamlainFlow',
  ({ block, toolbarContainer }) => {
    return {
      name: edgelessToolbarI18nT('toolbar.automation'),
      content: html`<edgeless-streamlain-flow-tool-button
        .edgeless=${block}
        .toolbarContainer=${toolbarContainer}
      ></edgeless-streamlain-flow-tool-button>`,
      priority: 79.5,
    };
  }
);
